import { useState, useCallback, useRef } from 'react';

/**
 * Orchestrates the Global Chat Panel state machine.
 *
 * States: CLOSED | OPEN_GLOBAL (floating panel) | OPEN_HOST (page controls visibility)
 *
 * Transitions:
 *   CLOSED      ── openGlobalChat()        ──> OPEN_GLOBAL | OPEN_HOST (delegates to host if registered)
 *   OPEN_GLOBAL ── closeGlobalChat()       ──> CLOSED      (preserves conversation)
 *   OPEN_GLOBAL ── dismissGlobalChat()     ──> CLOSED      (clears conversation)
 *   OPEN_GLOBAL ── navigate(nativeRoute)   ──> CLOSED      (useGlobalChatPanel auto-close)
 *   OPEN_HOST   ── host.close()            ──> CLOSED
 *   OPEN_HOST   ── unregisterChatHost()    ──> CLOSED
 *
 * Host: UCPPage registers { open, close }. openGlobalChat() delegates to host.open() when registered.
 * closeGlobalChat() does NOT call host.close() — avoids overriding UCP's open-by-default on navigate.
 *
 * Full docs: docs/architecture/GLOBAL-CHAT-PANEL-STATE-MACHINE.md
 */
export const useGlobalChatOrchestrator = ({ conversationsHook, messagesHook }) => {
  const [isGlobalChatOpen, setIsGlobalChatOpen] = useState(false);
  const chatHostRef = useRef(null);

  const clearChatState = useCallback(() => {
    conversationsHook.clearConversationSelection();
    messagesHook.cleanThread();
  }, [conversationsHook.clearConversationSelection, messagesHook.cleanThread]);

  const openGlobalChat = useCallback(() => {
    if (chatHostRef.current) {
      chatHostRef.current.open();
    } else {
      setIsGlobalChatOpen(true);
    }
  }, []);

  const closeGlobalChat = useCallback(() => {
    setIsGlobalChatOpen(false);
    // Do NOT call host.close() — when navigating to UCP, we want its chat open by default.
  }, []);

  const dismissGlobalChat = useCallback(() => {
    setIsGlobalChatOpen(false);
    clearChatState();
  }, [clearChatState]);

  const registerChatHost = useCallback((handlers) => {
    chatHostRef.current = handlers;
  }, []);

  const unregisterChatHost = useCallback(() => {
    chatHostRef.current = null;
  }, []);

  return {
    isGlobalChatOpen,
    openGlobalChat,
    closeGlobalChat,
    dismissGlobalChat,
    registerChatHost,
    unregisterChatHost,
  };
};
